import * as Sentry from '@sentry/react';
import { ErrorFallback } from './ErrorFallback';
import { captureException } from '@/config/posthog';

interface ErrorBoundaryOptions {
  fallback?: React.ComponentType<any>;
  feature?: string;
}

/**
 * HOC that wraps a component with Sentry's error boundary
 * @param Component The component to wrap
 * @param options Configuration options for the error boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: ErrorBoundaryOptions = {}
) {
  const { fallback = ErrorFallback, feature } = options;

  return Sentry.withErrorBoundary(Component, {
    fallback,
    showDialog: true, // Consistent with our App-level error boundary
    beforeCapture: (scope) => {
      if (feature) {
        scope.setTag("feature", feature);
      }
      scope.setTag("component_error", "true");
    },
    onError: (error) => {
      Sentry.addBreadcrumb({
        category: 'error',
        message: `Error in ${feature || 'component'}: ${error.message}`,
        level: 'error'
      });
      // Also capture to PostHog
      captureException(error, { feature: feature || 'component' });
    }
  });
} 